import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { safeSessionStorage } from "@/lib/storage";

const defaultCategory = "Ongoing";

const CategoryContext = createContext<{
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
}>({
  selectedCategory: defaultCategory,
  setSelectedCategory: () => {},
});

export const CategoryProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const location = useLocation();

  // Reset on hard reload
  useEffect(() => {
    const navEntry = performance.getEntriesByType("navigation")[0];
    const isReload =
      navEntry instanceof PerformanceNavigationTiming && navEntry.type === "reload";

    if (isReload) {
      safeSessionStorage.removeItem("selectedCategory");
      setSelectedCategory(defaultCategory);
    } else {
      const saved = safeSessionStorage.getItem("selectedCategory");
      if (saved) setSelectedCategory(saved);
    }
  }, []);

  // Sync sessionStorage on change
  useEffect(() => {
    safeSessionStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Reset when user navigates away from /projects
  useEffect(() => {
    if (!location.pathname.startsWith("/projects")) {
      setSelectedCategory(defaultCategory);
      safeSessionStorage.removeItem("selectedCategory");
    }
  }, [location]);

  return (
    <CategoryContext.Provider value={{ selectedCategory, setSelectedCategory }}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategory = () => useContext(CategoryContext);
